import { useJsApiLoader } from "@react-google-maps/api";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  googleMapsLibraries,
  nextPublicGoogleMapsApiKey,
} from "@app/constants";
import { ShopContext } from "@components/legacy/ShopProvider/context";
import { useLocalStorage } from "@hooks/useLocalStorage";

type SamedayPickupStatus = {
  error: string;
  isSamedayEligible: boolean;
  loading: boolean;
  userLocation?: google.maps.LatLng;
  distance?: string;
};

const SAMEDAY_PICKUP_INITIAL_STATUS = {
  error: "",
  isSamedayEligible: false,
  loading: false,
};

type UseSamedayPickupArgs = {
  productWarehouseLocation: string;
  inputElement: HTMLInputElement | null;
};

export const useSamedayPickup = ({
  productWarehouseLocation,
  inputElement,
}: UseSamedayPickupArgs) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: nextPublicGoogleMapsApiKey,
    libraries: googleMapsLibraries,
  });

  const [warehouseLocation, setWarehouseLocation] =
    useState<google.maps.LatLng>();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [samedayPickupStatus, setSamedayPickupStatus] =
    useLocalStorage<SamedayPickupStatus>(
      "samedayPickupStatus",
      SAMEDAY_PICKUP_INITIAL_STATUS
    );
  const { operatingHours, samedayOrNextdayPickupDistance } =
    useContext(ShopContext);

  const geocoder = useMemo(
    () => (isLoaded ? new google.maps.Geocoder() : null),
    [isLoaded]
  );

  const canPickupToday = useMemo(() => {
    if (!samedayPickupStatus?.isSamedayEligible) return false;

    const today = dayjs().format("dddd").toUpperCase();
    const currentDayOperatingHours = operatingHours
      ?.filter((item) => item?.hoursType === "SAMEDAY_PICKUP_CUTOFF")
      ?.find((item) => item?.dayOfWeek?.toUpperCase() === today);

    const cutoffTime = currentDayOperatingHours?.closeTime || "22:00:00";
    const cutoffDate = dayjs()
      .set("hour", parseInt(cutoffTime.split(":")[0], 10))
      .set("minute", parseInt(cutoffTime.split(":")[1], 10))
      .set("second", parseInt(cutoffTime.split(":")[2], 10))
      .toDate();

    return dayjs().toDate() <= dayjs(cutoffDate).subtract(2, "hours").toDate();
  }, [operatingHours, samedayPickupStatus?.isSamedayEligible]);

  useEffect(() => {
    if (!geocoder) return;

    geocoder.geocode(
      { address: productWarehouseLocation },
      (results, status) => {
        if (status === "OK" && results?.length) {
          setWarehouseLocation(results[0].geometry.location);
        }
      }
    );
  }, [productWarehouseLocation, geocoder]);

  const calculateDistance = useCallback(
    (
      userLocation: google.maps.LatLng,
      warehouseGeolocation: google.maps.LatLng
    ) => {
      if (!google.maps.geometry.spherical) {
        setSamedayPickupStatus({
          error: "Failed to load geometry. Please try again.",
          isSamedayEligible: false,
          loading: false,
        });
        return;
      }

      const distanceInKm =
        google.maps.geometry.spherical.computeDistanceBetween(
          userLocation,
          warehouseGeolocation
        ) / 1000;

      const roundUpToNearest100 = (distance: number): number =>
        Math.ceil(distance / 100) * 100;

      setSamedayPickupStatus({
        error: isNaN(distanceInKm)
          ? "Failed to calculate distance. Please try again."
          : "",
        isSamedayEligible:
          distanceInKm <= Number(samedayOrNextdayPickupDistance || "25"),
        loading: false,
        distance:
          distanceInKm <= 100
            ? distanceInKm.toFixed(0)
            : roundUpToNearest100(distanceInKm).toString(),
        userLocation,
      });
    },
    []
  );

  useEffect(() => {
    const userLocation = samedayPickupStatus?.userLocation;

    if (warehouseLocation && userLocation) {
      calculateDistance(userLocation, warehouseLocation);
    }
  }, [calculateDistance, warehouseLocation]);

  const handlePlaceChanged = useCallback(() => {
    if (!inputElement) return;
    const autocomplete = new google.maps.places.Autocomplete(inputElement, {
      types: ["geocode"],
      componentRestrictions: { country: "au" },
    });

    const placeChangedListener = () => {
      // setSamedayPickupStatus((prev) => ({ ...prev, loading: true }));
      setSamedayPickupStatus({
        error: "",
        isSamedayEligible: false,
        loading: true,
      });

      const userLocation = autocomplete.getPlace()?.geometry?.location;

      if (userLocation && warehouseLocation) {
        calculateDistance(userLocation, warehouseLocation);
      } else {
        setSamedayPickupStatus({
          error: "Unable to find your location. Please try again.",
          isSamedayEligible: false,
          loading: false,
        });
      }
    };

    autocomplete.addListener("place_changed", placeChangedListener);

    return () => google.maps.event.clearInstanceListeners(autocomplete);
  }, [inputElement, warehouseLocation, calculateDistance]);

  useEffect(() => handlePlaceChanged(), [handlePlaceChanged]);

  const handleUseCurrentLocation = useCallback(() => {
    if (!geocoder) return;
    // setSamedayPickupStatus((prev) => ({ ...prev, loading: true }));
    setSamedayPickupStatus({
      error: "",
      isSamedayEligible: false,
      loading: true,
    });

    if (!navigator.geolocation) {
      setSamedayPickupStatus({
        error: "Geolocation is not supported by this browser.",
        isSamedayEligible: false,
        loading: false,
      });
      return;
    }

    const onGeolocationSuccess = (position: GeolocationPosition) => {
      const userLocation = new google.maps.LatLng(
        position.coords.latitude,
        position.coords.longitude
      );

      if (!warehouseLocation) {
        // setSamedayPickupStatus((prev) => ({ ...prev, loading: false }));
        setSamedayPickupStatus({
          error: "",
          isSamedayEligible: false,
          loading: false,
        });
        return;
      }

      geocoder.geocode({ location: userLocation }, (results, status) => {
        if (status === "OK" && results?.length && inputElement) {
          inputElement.value = results[0].formatted_address;
          calculateDistance(userLocation, warehouseLocation);
        } else {
          setSamedayPickupStatus({
            error: "Failed to retrieve your current address. Please try again.",
            isSamedayEligible: false,
            loading: false,
          });
        }
      });
    };

    const onGeolocationError = (error: GeolocationPositionError) => {
      const errorMessages = {
        [error.PERMISSION_DENIED]: "Request for Geolocation was denied.",
        [error.POSITION_UNAVAILABLE]: "Location information is unavailable.",
        [error.TIMEOUT]:
          "Request to get the location timed out. Please try again.",
      };

      setSamedayPickupStatus({
        error: errorMessages[error.code] || "An unknown error occurred.",
        isSamedayEligible: false,
        loading: false,
      });
    };

    navigator.geolocation.getCurrentPosition(
      onGeolocationSuccess,
      onGeolocationError,
      {
        timeout: 5000,
      }
    );
  }, [warehouseLocation, inputElement, calculateDistance, geocoder]);

  const reset = () => {
    setSamedayPickupStatus(SAMEDAY_PICKUP_INITIAL_STATUS);
  };

  return {
    canPickupToday,
    distance: samedayPickupStatus?.distance || "",
    errorMessage: samedayPickupStatus?.error || "",
    handleUseCurrentLocation,
    isSamedayEligible: samedayPickupStatus?.isSamedayEligible || false,
    isDrawerOpen,
    loading: samedayPickupStatus?.loading || false,
    reset,
    setIsDrawerOpen,
    isLoaded,
  };
};
